import { CheckBadgeIcon, MapPinIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useState, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import MapLogin from "./maplogin";
import { parse, format } from "date-fns";
import { TruckIcon } from "@heroicons/react/24/solid";
import { AuthContext } from "../AuthContext";
import WorkCoach from "./workcoach";
import Maps from "./maps";
import { useWebSocket } from "../WebSocketContext";

export default function Online() {
  const [clockedInStaff, setClockedInStaff] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState(null);
  const user = useContext(AuthContext); // Get the user object from AuthContext
  const { sendMessage } = useWebSocket();

  const fetchClockinData = useCallback(async () => {
    if (user.access !== "admin" && user.access !== "manager") return;

    try {
      const response = await fetch('https://api.insightos.co.uk/api/clockins', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`API response not OK: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        const clockedIn = data.data.filter(staff => staff.ClockedIn === 1 && staff.ClockedOut === 0);
        setClockedInStaff(clockedIn);
      }
    } catch (error) {
      console.error("Failed to fetch clockin data:", error);
    }
  }, [user.access]);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.detail;
      if (data.type === "ping") return;
      if (data.success) {
        fetchClockinData();
      }
    };

    window.addEventListener('insightos-websocket-message', handleMessage);
    fetchClockinData();
    sendMessage({ type: 'ping' });

    return () => {
      window.removeEventListener('insightos-websocket-message', handleMessage);
    };
  }, [fetchClockinData, sendMessage]);

  const handleMapClick = (geoTag) => {
    if (!geoTag) {
      setError("No location data available for this staff.");
      return;
    }
    const [latitude, longitude] = geoTag.split(",").map(Number);
    setSelectedLocation({ latitude, longitude });
  };

  const closeMap = () => {
    setSelectedLocation(null);
    setError(null);
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A"; // Handle missing or null values
  
    const date = parse(timeString, "HH:mm:ss", new Date()); // Parse the time string
    if (isNaN(date.getTime())) return "Invalid Time"; // Handle invalid parsing
  
    return format(date, "HH:mm"); // Convert to HH:mm format
  };

  // Function to get meeting sequence status and styling
  const getMeetingSequenceInfo = (sequence) => {
    switch (sequence) {
      case 1:
        return {
          status: "Traveling",
          icon: "🚗",
          bgColor: "bg-yellow-50",
          textColor: "text-yellow-700",
          borderColor: "border-yellow-200",
          badgeColor: "bg-yellow-100 text-yellow-800"
        };
      case 2:
        return {
          status: "At Meeting",
          icon: "📍",
          bgColor: "bg-blue-50",
          textColor: "text-blue-700",
          borderColor: "border-blue-200",
          badgeColor: "bg-blue-100 text-blue-800"
        };
      case 3:
        return {
          status: "Back in Office",
          icon: "🏢",
          bgColor: "bg-green-50",
          textColor: "text-green-700",
          borderColor: "border-green-200",
          badgeColor: "bg-green-100 text-green-800"
        };
      case 5:
        return {
          status: "In Meeting",
          icon: "🤝",
          bgColor: "bg-purple-50",
          textColor: "text-purple-700",
          borderColor: "border-purple-200",
          badgeColor: "bg-purple-100 text-purple-800"
        };
      default:
        return {
          status: "In Office",
          icon: "💼",
          bgColor: "bg-gray-50",
          textColor: "text-gray-700",
          borderColor: "border-gray-200",
          badgeColor: "bg-gray-100 text-gray-800"
        };
    }
  };

  // Function to get the appropriate background color for table rows
  const getTableRowBg = (staff) => {
    if (staff.Break) return "bg-red-50";
    const meetingInfo = getMeetingSequenceInfo(staff.MeetingSequence);
    return meetingInfo.bgColor;
  };
  
  

  if (selectedLocation) {
    return <MapLogin selectedLocation={selectedLocation} onClose={closeMap} />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
     
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Staff Online: {clockedInStaff.length}</h2>
      
      {/* Error message */}
      {error && (
        <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-md border border-red-200 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={closeMap}
            data-component="online-staff-map"
            data-feature="dismiss_location_error"
            className="text-red-700 font-medium hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Desktop View - Table */}
      <div className="hidden md:block mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 bg-white shadow-sm rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Staff Name</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Clock-In Time</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Break Time</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Meeting Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">View Location</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {clockedInStaff.map((staff) => {
              const meetingInfo = getMeetingSequenceInfo(staff.MeetingSequence);
              return (
                <tr key={staff.id} className={getTableRowBg(staff)}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                    <Link to="/dashboard" className="font-medium text-blue-900 hover:underline">
                      {staff.StaffName}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{staff.Department}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatTime(staff.ClockIn)}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {(!staff.BreakStart || staff.BreakStart === 'N/A') ? (
                      <span className="text-gray-500">N/A</span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-gray-700">{staff.BreakStart} - {staff.BreakEnd || 'N/A'}</span>
                        <span className="text-xs text-gray-500">{(staff.StaffBreak || 0)} mins</span>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      staff.Break ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                    }`}>
                      {staff.Break ? "🔴 On Break" : "🟢 Working"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meetingInfo.badgeColor}`}>
                      {meetingInfo.icon} {meetingInfo.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <button
                      className="text-blue-500 hover:text-blue-700 focus:outline-none"
                      onClick={() => handleMapClick(staff.GeoTagClockin)}
                      data-component="online-staff-table"
                      data-feature="view_staff_location"
                      data-staff={staff.StaffName}
                    >
                      <MapPinIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {clockedInStaff.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 py-4 text-sm text-gray-500 text-center">
                  No staff members currently clocked in.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile View - Cards */}
      <div className="md:hidden space-y-4">
        {clockedInStaff.length === 0 ? (
          <div className="bg-white p-4 rounded-lg shadow text-center text-gray-500">
            No staff members currently clocked in.
          </div>
        ) : (
          clockedInStaff.map((staff) => {
            const meetingInfo = getMeetingSequenceInfo(staff.MeetingSequence);
            const borderColor = staff.Break ? "border-red-400" : meetingInfo.borderColor.replace('border-', 'border-l-4 border-');
            const bgColor = staff.Break ? "bg-red-50" : meetingInfo.bgColor;
            
            return (
              <div 
                key={staff.id} 
                className={`rounded-lg shadow overflow-hidden ${borderColor}`}
              >
                <div className={`p-4 ${bgColor}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <Link to="/dashboard" className="text-base font-medium text-blue-900 hover:underline">
                        {staff.StaffName}
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">{staff.Department}</p>
                    </div>
                    <div>
                      <button 
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => handleMapClick(staff.GeoTagClockin)}
                        data-component="online-staff-card"
                        data-feature="view_staff_location"
                        data-staff={staff.StaffName}
                      >
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        View Location
                      </button>
                    </div>
                  </div>
                  
                  {/* Status Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      staff.Break ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                    }`}>
                      {staff.Break ? "🔴 On Break" : "🟢 Working"}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meetingInfo.badgeColor}`}>
                      {meetingInfo.icon} {meetingInfo.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-2">
                    <div className="bg-white rounded p-2">
                      <p className="text-xs text-gray-500">Clock-In Time</p>
                      <p className="font-medium">{formatTime(staff.ClockIn)}</p>
                    </div>
                    <div className="bg-white rounded p-2">
                      <p className="text-xs text-gray-500">Break</p>
                      {(!staff.BreakStart || staff.BreakStart === 'N/A') ? (
                        <p className="font-medium text-gray-500">N/A</p>
                      ) : (
                        <div>
                          <p className="font-medium">{staff.BreakStart} - {staff.BreakEnd || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{(staff.StaffBreak || 0)} mins</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    <div className="mt-10 mb-10">
      <Maps />
    </div>
    </div>
  );
}
